<!-- Hero Section with Background Image -->
<div class="shows-hero">
  <div class="shows-hero-bg"></div>
  <div class="shows-hero-overlay"></div>
  <div class="container-xxl py-10 position-relative">
    <!-- Page Header -->
    <div class="d-flex flex-wrap flex-stack mb-10">
      <h1 class="fw-bold my-2 text-white"><i class="fa fa-medal me-3" style="color: #FFD700;"></i>{{ 'competitions.title' | translate }}</h1>
    </div>

    <!-- Filters Section -->
    <div class="card mb-0 shadow-lg">
      <div class="card-body">
        <div class="row g-5 align-items-end">
          <!-- Search Filter -->
          <div class="col-md-3">
            <app-input-component
              [type]="'text'"
              [(value)]="searchQuery"
              [label]="'competitions.search' | translate"
              [placeholder]="'competitions.searchPlaceholder' | translate"
              [floating]="true"
              (valueChange)="onSearchChange()"
            />
          </div>

          <!-- Discipline/Category Filter -->
          <div class="col-md-2">
            <app-selectorini
              [options]="disciplineOptions"
              [displayKey]="'label'"
              [valueKey]="'value'"
              [(value)]="selectedDiscipline"
              [label]="'competitions.discipline' | translate"
              [placeholder]="'competitions.allDisciplines' | translate"
              [floating]="true"
              (valueChange)="onDisciplineChange()"
            />
          </div>

          <!-- Company Filter -->
          <div class="col-md-2">
            <app-selectorini
              [options]="companies"
              [displayKey]="'company_name'"
              [valueKey]="'company_id'"
              [(value)]="selectedCompanyId"
              [label]="'competitions.organizer' | translate"
              [placeholder]="'competitions.allOrganizers' | translate"
              [floating]="true"
              (valueChange)="onCompanyChange()"
            />
          </div>

          <!-- Date Range Filter -->
          <div class="col-md-2">
            <app-date-range-picker
              [(fromDate)]="dateFrom"
              [(toDate)]="dateTo"
              [label]="'competitions.dateRange' | translate"
              [placeholder]="'competitions.selectDates' | translate"
              (rangeChange)="onDateFilterChange()"
            />
          </div>

          <!-- Sort by Nearest Button -->
          <div class="col-auto">
            <button
              class="btn"
              [class.btn-primary]="sortByDistance"
              [class.btn-light]="!sortByDistance"
              (click)="toggleSortByDistance()"
              [disabled]="!userLocation"
              style="height: 50px;"
              [title]="userLocation ? ('competitions.sortByNearest' | translate) : ('competitions.waitingForLocation' | translate)"
            >
              @if (locationLoading) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              } @else {
                <i class="fa fa-location-crosshairs me-2"></i>
              }
              {{ 'competitions.nearest' | translate }}
            </button>
          </div>

          <!-- Clear Filters Button -->
          <div class="col-auto">
            <app-button
              [variant]="'neutral'"
              [iconClass]="'fa fa-times'"
              (clicked)="clearFilters()"
              [title]="'competitions.clearFilters' | translate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container-xxl py-10">

  <!-- Map Section -->
  @if (!loading && (filteredEvents.length > 0 || filteredNryfEvents.length > 0)) {
    <div class="mb-10">
      <app-location-map
        [markers]="mapMarkers"
        [config]="mapConfig"
        [loading]="geocodingInProgress"
        (markerClick)="onMapMarkerClick($event)"
        (infoWindowAction)="onMapInfoWindowAction($event)"
      />
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="text-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'competitions.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'competitions.loading' | translate }}</p>
    </div>
  }

  <!-- No Results -->
  @if (!loading && filteredEvents.length === 0) {
    <div class="card">
      <div class="card-body text-center py-20">
        <i class="fa fa-calendar-xmark fs-3x text-muted mb-5"></i>
        <h3 class="text-muted">{{ 'competitions.noResults' | translate }}</h3>
        <p class="text-muted">{{ 'competitions.noResultsHint' | translate }}</p>
      </div>
    </div>
  }

  <!-- Upcoming Events -->
  @if (!loading && upcomingEvents.length > 0) {
    <div class="mb-10">
      <div class="d-flex flex-wrap flex-stack mb-6">
        <h2 class="fw-bold my-2">
          <i class="fa fa-calendar-check text-success me-3"></i>{{ 'competitions.upcoming' | translate }}
        </h2>
        <app-button
          [variant]="'primary'"
          [label]="'common.createEvent' | translate"
          [iconClass]="'fa fa-plus'"
          (clicked)="openCreateEvent()"
        />
      </div>
      <div class="row g-6 g-xl-9">
        @for (event of upcomingEvents; track event.event_id) {
          <div class="col-md-6 col-lg-4">
            <div class="card h-100 event-card" [id]="'event-card-' + event.event_id">
            <!-- Event Image -->
            <div class="event-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top event-image" [alt]="event.event_name"/>
              } @else {
                <div class="event-image-placeholder bg-light d-flex align-items-center justify-content-center">
                  <i class="fa fa-calendar fs-3x text-muted"></i>
                </div>
              }

              <!-- Event Status Badge -->
              <div class="position-absolute top-0 end-0 m-3">
                @if (isEventUpcoming(event.start_date)) {
                  <span class="badge badge-success badge-lg">{{ 'event.badge.upcoming' | translate }}</span>
                } @else if (isEventPast(event.start_date)) {
                  <span class="badge badge-secondary badge-lg">{{ 'event.badge.past' | translate }}</span>
                }
              </div>

              <!-- Distance Badge -->
              @if (userLocation && getEventDistance(event) !== null) {
                <div class="position-absolute top-0 start-0 m-3">
                  <span class="badge badge-primary badge-lg">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getEventDistance(event)) }}
                  </span>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Category Badge -->
              <div class="mb-3">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">{{ getCategoryDisplay(event) }}</span>
              </div>

              <!-- Event Name -->
              <h3 class="card-title fw-bold fs-4 mb-2">{{ event.event_name }}</h3>

              <!-- Organiser -->
              @if (event.company) {
                <div class="mb-3">
                  @if (event.company.is_personal) {
                    <span class="text-muted fs-7">
                      <i class="fa fa-user me-1"></i>{{ event.company.company_name }}
                    </span>
                  } @else {
                    <a class="text-muted fs-7 text-decoration-none organiser-link" [routerLink]="['/organisations', event.company.company_id]">
                      <i class="fa fa-building me-1"></i>{{ event.company.company_name }}
                    </a>
                  }
                </div>
              }

              <!-- Event Details -->
              <div class="mb-5 mt-auto">
                <div class="row g-3">
                  <!-- Left Column -->
                  <div class="col-6">
                    <!-- Dates -->
                    @if (event.start_date) {
                      <div class="d-flex align-items-start mb-3">
                        <i class="fa fa-calendar text-primary me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.start_date) }}</div>
                          @if (event.end_date && event.start_date !== event.end_date) {
                            <div class="text-muted fs-8">{{ 'event.dateTo' | translate }} {{ formatDateDMYDots(event.end_date) }}</div>
                          }
                        </div>
                      </div>
                    }

                    <!-- Location -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-location-dot text-primary me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ event.city }}, {{ event.country }}</div>
                        @if (event.address) {
                          <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-muted fs-8 address-link text-decoration-none">
                            {{ event.address }}
                            <i class="fa fa-external-link ms-1 fs-9"></i>
                          </a>
                        }
                      </div>
                    </div>

                    <!-- Registration Deadline -->
                    @if (event.registration_deadline) {
                      <div class="d-flex align-items-start">
                        <i class="fa fa-clock text-warning me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="text-muted fs-8">{{ 'event.regDeadline' | translate }}</div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Right Column -->
                  <div class="col-6">
                    <!-- Available Classes (Competition events only) -->
                    @if (isCompetitionEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventClasses(event.event_id)">
                        <i class="fa fa-list text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.classes' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-list me-2"></i>{{ 'event.availableClasses' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingClasses[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventClasses[event.event_id] && eventClasses[event.event_id].length > 0) {
                                <ul>
                                  @for (eventClass of eventClasses[event.event_id]; track eventClass.event_class_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ eventClass.class_name }}</strong>
                                      @if (eventClass.price) {
                                        <span class="text-muted ms-2">({{ eventClass.price }} {{ eventClass.price_currency || 'kr' }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noClasses' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Packages (Training events only) -->
                    @if (isTrainingEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventPackages(event.event_id)">
                        <i class="fa fa-box-open text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.packages' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-box-open me-2"></i>{{ 'event.availablePackages' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingPackages[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventPackages[event.event_id] && eventPackages[event.event_id].length > 0) {
                                <ul>
                                  @for (pkg of eventPackages[event.event_id]; track pkg.package_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ pkg.package_name }}</strong>
                                      <span class="ms-2" [ngClass]="getPackageAvailabilityBadge(pkg).class" style="font-size: 10px; padding: 2px 6px; border-radius: 4px;">
                                        {{ getPackageAvailabilityBadge(pkg).text }}
                                      </span>
                                      @if (pkg.price_cents > 0) {
                                        <span class="text-muted ms-2">({{ formatPackagePrice(pkg.price_cents, pkg.currency) }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noPackages' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Services (All event types) -->
                    <div class="d-flex align-items-start hover-info-container" (mouseenter)="loadEventServices(event.event_id)">
                      <i class="fa fa-concierge-bell text-success me-2 fs-6 mt-1"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold fs-7">{{ 'event.services' | translate }}</div>
                        <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                      </div>
                      <div class="hover-popup">
                        <div class="hover-popup-content">
                          <div class="hover-popup-header">
                            <i class="fa fa-concierge-bell me-2"></i>{{ 'event.availableServices' | translate }}
                          </div>
                          <div class="hover-popup-body">
                            @if (loadingServices[event.event_id]) {
                              <div class="text-center py-2">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                  <span class="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            } @else if (eventServices[event.event_id] && eventServices[event.event_id].length > 0) {
                              <ul>
                                @for (service of eventServices[event.event_id]; track service.event_service_id) {
                                  <li>
                                    <i class="fa fa-circle-check"></i>
                                    <strong>{{ service.event_service_name }}</strong>
                                    @if (service.max_num_registrations) {
                                      <span class="service-slots-badge">
                                        {{ getServiceAvailableSlots(service) }}/{{ service.max_num_registrations }} {{ 'event.slots' | translate }}
                                      </span>
                                    }
                                    @if (service.price) {
                                      <span class="text-muted ms-2">({{ service.price }} {{ service.price_currency || 'kr' }})</span>
                                    }
                                  </li>
                                }
                              </ul>
                            } @else {
                              <div class="text-muted text-center py-2">{{ 'event.noServices' | translate }}</div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Button -->
              <div class="mt-auto text-center">
                <app-button
                  [variant]="'primary'"
                  [label]="'competitions.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                />
              </div>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  }

  <!-- NRYF Events Section -->
  @if (showNryfSection) {
    <section class="nryf-events-section mt-10">
      <!-- NRYF Divider -->
      <div class="separator-section mb-8">
        <div class="d-flex align-items-center">
          <div class="flex-grow-1 border-bottom border-gray-300"></div>
          <div class="px-5">
            <span class="badge badge-light-dark badge-lg fs-6 px-4 py-2">
              <i class="fa fa-horse me-2"></i>NRYF
            </span>
          </div>
          <div class="flex-grow-1 border-bottom border-gray-300"></div>
        </div>
      </div>

      <!-- NRYF Header -->
      <div class="nryf-header mb-6">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-4">
          <div class="d-flex align-items-center gap-4">
            <div class="nryf-logo-container">
              <i class="fa fa-horse-head fs-2x text-primary"></i>
            </div>
            <div>
              <h3 class="mb-1 fw-bold">{{ 'shows.nryf.title' | translate }}</h3>
              <p class="text-muted mb-0 fs-7">{{ 'shows.nryf.subtitle' | translate }}</p>
            </div>
          </div>
          <a href="https://nryfstevne.no/events" target="_blank" class="btn btn-light btn-sm">
            {{ 'shows.nryf.viewAll' | translate }}
            <i class="fa fa-external-link ms-2"></i>
          </a>
        </div>
      </div>

      <!-- NRYF Loading -->
      @if (loadingNryf) {
        <div class="text-center py-10">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-3">{{ 'competitions.loading' | translate }}</p>
        </div>
      }

      <!-- NRYF No Events -->
      @if (!loadingNryf && filteredNryfEvents.length === 0) {
        <div class="card bg-light">
          <div class="card-body text-center py-10">
            <i class="fa fa-calendar-xmark fs-3x text-muted mb-4"></i>
            <p class="text-muted mb-0">{{ 'shows.nryf.noEvents' | translate }}</p>
          </div>
        </div>
      }

      <!-- NRYF Events List -->
      @if (!loadingNryf && filteredNryfEvents.length > 0) {
        <div class="nryf-list">
          @for (event of displayedNryfEvents; track event.id) {
            <div class="nryf-list-item" (click)="navigateToNryfEvent(event.id)">
              <!-- Date Block -->
              <div class="nryf-date-block">
                <div class="nryf-date-day">{{ formatNryfDay(event.startDate) }}</div>
                <div class="nryf-date-month">{{ formatNryfMonth(event.startDate) }}</div>
                @if (event.endDate && event.endDate !== event.startDate) {
                  <div class="nryf-date-range">- {{ formatNryfDay(event.endDate) }}.{{ formatNryfMonth(event.endDate) }}</div>
                }
              </div>

              <!-- Event Info -->
              <div class="nryf-event-info">
                <div class="nryf-event-name">{{ event.name }}</div>
                <div class="nryf-event-meta">
                  @if (event.place || event.venueAddress) {
                    <span class="nryf-meta-item">
                      <i class="fa fa-map-marker-alt"></i>
                      {{ event.place || event.venueAddress }}
                    </span>
                  }
                  @if (event.organiser) {
                    <span class="nryf-meta-item">
                      <i class="fa fa-building"></i>
                      {{ event.organiser.name || event.organiser.fullName }}
                    </span>
                  }
                </div>
              </div>

              <!-- Discipline Column -->
              <div class="nryf-discipline-col">
                @if (event.disciplineId && nryfMasterdata) {
                  <span class="nryf-tag nryf-tag-discipline">{{ getNryfDisciplineName(event.disciplineId) }}</span>
                }
              </div>

              <!-- Levels Column -->
              <div class="nryf-levels-col">
                @if (event.competitionLevels && event.competitionLevels.length > 0) {
                  @for (level of event.competitionLevels.slice(0, 2); track level.id) {
                    <span class="nryf-tag nryf-tag-level">{{ level.name }}</span>
                  }
                }
              </div>

              <!-- Status & Arrow -->
              <div class="nryf-status">
                @if (isNryfRegistrationOpen(event)) {
                  <span class="nryf-status-badge nryf-status-open">{{ 'shows.nryf.status.open' | translate }}</span>
                } @else {
                  <span class="nryf-status-badge nryf-status-closed">{{ 'shows.nryf.status.closed' | translate }}</span>
                }
                <i class="fa fa-chevron-right nryf-arrow"></i>
              </div>
            </div>
          }
        </div>

        <!-- Show All / Show Less Button -->
        @if (hasMoreNryfEvents || showAllNryfEvents) {
          <div class="nryf-show-more">
            <button class="btn-show-more" (click)="toggleShowAllNryfEvents()">
              @if (showAllNryfEvents) {
                <i class="fa fa-chevron-up me-2"></i>
                {{ 'shows.nryf.showLess' | translate }}
              } @else {
                <i class="fa fa-chevron-down me-2"></i>
                {{ 'shows.nryf.showAll' | translate }} ({{ hiddenNryfEventsCount }} {{ 'shows.nryf.more' | translate }})
              }
            </button>
          </div>
        }
      }
    </section>
  }

  <!-- Divider -->
  @if (!loading && upcomingEvents.length > 0 && pastEvents.length > 0) {
    <div class="separator-section my-10">
      <div class="d-flex align-items-center">
        <div class="flex-grow-1 border-bottom border-gray-300"></div>
        <div class="px-5">
          <span class="badge badge-light-secondary badge-lg">{{ 'competitions.pastEvents' | translate }}</span>
        </div>
        <div class="flex-grow-1 border-bottom border-gray-300"></div>
      </div>
    </div>
  }

  <!-- Past Events -->
  @if (!loading && pastEvents.length > 0) {
    <div class="mb-10">
      @if (upcomingEvents.length === 0) {
        <h2 class="fw-bold mb-6">
          <i class="fa fa-calendar-xmark text-secondary me-3"></i>{{ 'competitions.past' | translate }}
        </h2>
      }
      <div class="row g-6 g-xl-9">
        @for (event of pastEvents; track event.event_id) {
          <div class="col-md-6 col-lg-4">
            <div class="card h-100 event-card" [id]="'event-card-' + event.event_id">
            <!-- Event Image -->
            <div class="event-image-container position-relative">
              @if (event.header_url) {
                <img [src]="event.header_url" class="card-img-top event-image" [alt]="event.event_name"/>
              } @else {
                <div class="event-image-placeholder bg-light d-flex align-items-center justify-content-center">
                  <i class="fa fa-calendar fs-3x text-muted"></i>
                </div>
              }

              <!-- Event Status Badge -->
              <div class="position-absolute top-0 end-0 m-3">
                @if (isEventUpcoming(event.start_date)) {
                  <span class="badge badge-success badge-lg">{{ 'event.badge.upcoming' | translate }}</span>
                } @else if (isEventPast(event.start_date)) {
                  <span class="badge badge-secondary badge-lg">{{ 'event.badge.past' | translate }}</span>
                }
              </div>

              <!-- Distance Badge -->
              @if (userLocation && getEventDistance(event) !== null) {
                <div class="position-absolute top-0 start-0 m-3">
                  <span class="badge badge-primary badge-lg">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getEventDistance(event)) }}
                  </span>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Event Category Badge -->
              <div class="mb-3">
                <span class="badge" [ngClass]="getCategoryBadgeClass(event)">{{ getCategoryDisplay(event) }}</span>
              </div>

              <!-- Event Name -->
              <h3 class="card-title fw-bold fs-4 mb-2">{{ event.event_name }}</h3>

              <!-- Organiser -->
              @if (event.company) {
                <div class="mb-3">
                  @if (event.company.is_personal) {
                    <span class="text-muted fs-7">
                      <i class="fa fa-user me-1"></i>{{ event.company.company_name }}
                    </span>
                  } @else {
                    <a class="text-muted fs-7 text-decoration-none organiser-link" [routerLink]="['/organisations', event.company.company_id]">
                      <i class="fa fa-building me-1"></i>{{ event.company.company_name }}
                    </a>
                  }
                </div>
              }

              <!-- Event Details -->
              <div class="mb-5 mt-auto">
                <div class="row g-3">
                  <!-- Left Column -->
                  <div class="col-6">
                    <!-- Dates -->
                    @if (event.start_date) {
                      <div class="d-flex align-items-start mb-3">
                        <i class="fa fa-calendar text-primary me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.start_date) }}</div>
                          @if (event.end_date && event.start_date !== event.end_date) {
                            <div class="text-muted fs-8">{{ 'event.dateTo' | translate }} {{ formatDateDMYDots(event.end_date) }}</div>
                          }
                        </div>
                      </div>
                    }

                    <!-- Location -->
                    <div class="d-flex align-items-start mb-3">
                      <i class="fa fa-location-dot text-primary me-2 fs-6 mt-1"></i>
                      <div>
                        <div class="fw-semibold fs-7">{{ event.city }}, {{ event.country }}</div>
                        @if (event.address) {
                          <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-muted fs-8 address-link text-decoration-none">
                            {{ event.address }}
                            <i class="fa fa-external-link ms-1 fs-9"></i>
                          </a>
                        }
                      </div>
                    </div>

                    <!-- Registration Deadline -->
                    @if (event.registration_deadline) {
                      <div class="d-flex align-items-start">
                        <i class="fa fa-clock text-warning me-2 fs-6 mt-1"></i>
                        <div>
                          <div class="text-muted fs-8">{{ 'event.regDeadline' | translate }}</div>
                          <div class="fw-semibold fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Right Column -->
                  <div class="col-6">
                    <!-- Available Classes (Competition events only) -->
                    @if (isCompetitionEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventClasses(event.event_id)">
                        <i class="fa fa-list text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.classes' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-list me-2"></i>{{ 'event.availableClasses' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingClasses[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventClasses[event.event_id] && eventClasses[event.event_id].length > 0) {
                                <ul>
                                  @for (eventClass of eventClasses[event.event_id]; track eventClass.event_class_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ eventClass.class_name }}</strong>
                                      @if (eventClass.price) {
                                        <span class="text-muted ms-2">({{ eventClass.price }} {{ eventClass.price_currency || 'kr' }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noClasses' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Packages (Training events only) -->
                    @if (isTrainingEvent(event)) {
                      <div class="d-flex align-items-start mb-3 hover-info-container" (mouseenter)="loadEventPackages(event.event_id)">
                        <i class="fa fa-box-open text-info me-2 fs-6 mt-1"></i>
                        <div class="flex-grow-1">
                          <div class="fw-semibold fs-7">{{ 'event.packages' | translate }}</div>
                          <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                        </div>
                        <div class="hover-popup">
                          <div class="hover-popup-content">
                            <div class="hover-popup-header">
                              <i class="fa fa-box-open me-2"></i>{{ 'event.availablePackages' | translate }}
                            </div>
                            <div class="hover-popup-body">
                              @if (loadingPackages[event.event_id]) {
                                <div class="text-center py-2">
                                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              } @else if (eventPackages[event.event_id] && eventPackages[event.event_id].length > 0) {
                                <ul>
                                  @for (pkg of eventPackages[event.event_id]; track pkg.package_id) {
                                    <li>
                                      <i class="fa fa-circle-check"></i>
                                      <strong>{{ pkg.package_name }}</strong>
                                      <span class="ms-2" [ngClass]="getPackageAvailabilityBadge(pkg).class" style="font-size: 10px; padding: 2px 6px; border-radius: 4px;">
                                        {{ getPackageAvailabilityBadge(pkg).text }}
                                      </span>
                                      @if (pkg.price_cents > 0) {
                                        <span class="text-muted ms-2">({{ formatPackagePrice(pkg.price_cents, pkg.currency) }})</span>
                                      }
                                    </li>
                                  }
                                </ul>
                              } @else {
                                <div class="text-muted text-center py-2">{{ 'event.noPackages' | translate }}</div>
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Available Services (All event types) -->
                    <div class="d-flex align-items-start hover-info-container" (mouseenter)="loadEventServices(event.event_id)">
                      <i class="fa fa-concierge-bell text-success me-2 fs-6 mt-1"></i>
                      <div class="flex-grow-1">
                        <div class="fw-semibold fs-7">{{ 'event.services' | translate }}</div>
                        <div class="text-muted fs-8">{{ 'event.hoverToView' | translate }}</div>
                      </div>
                      <div class="hover-popup">
                        <div class="hover-popup-content">
                          <div class="hover-popup-header">
                            <i class="fa fa-concierge-bell me-2"></i>{{ 'event.availableServices' | translate }}
                          </div>
                          <div class="hover-popup-body">
                            @if (loadingServices[event.event_id]) {
                              <div class="text-center py-2">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                  <span class="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            } @else if (eventServices[event.event_id] && eventServices[event.event_id].length > 0) {
                              <ul>
                                @for (service of eventServices[event.event_id]; track service.event_service_id) {
                                  <li>
                                    <i class="fa fa-circle-check"></i>
                                    <strong>{{ service.event_service_name }}</strong>
                                    @if (service.max_num_registrations) {
                                      <span class="service-slots-badge">
                                        {{ getServiceAvailableSlots(service) }}/{{ service.max_num_registrations }} {{ 'event.slots' | translate }}
                                      </span>
                                    }
                                    @if (service.price) {
                                      <span class="text-muted ms-2">({{ service.price }} {{ service.price_currency || 'kr' }})</span>
                                    }
                                  </li>
                                }
                              </ul>
                            } @else {
                              <div class="text-muted text-center py-2">{{ 'event.noServices' | translate }}</div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Button -->
              <div class="mt-auto text-center">
                <app-button
                  [variant]="'primary'"
                  [label]="'competitions.viewDetails' | translate"
                  [routerLink]="'/event-details/' + event.event_id"
                />
              </div>
            </div>
          </div>
        </div>
        }
      </div>
    </div>
  }
</div>
